import React, { useEffect, useState, useContext } from 'react'
import { Grid, makeStyles, Tile, InputField, Button } from '@paintscout/ui'
import { DialogStackContext, DropdownSelect } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import type { ProductOption, BulkEditDetails, BulkOperation } from '@paintscout/util/builder'
import { getObjectLabels } from '@paintscout/util/builder'
import type { QuoteDocument, OptionsDocument } from 'paintscout'
import ProductSelectDialog from '../ProductSelectDialog'
import { CloseButton } from '../../common'

const LINE_ITEMS_VALUE = 'line-items'

const useStyles = makeStyles((theme) => ({
  root: {},
  editItemSection: {
    position: 'relative',
    alignItems: 'center',
    margin: '0.5em 0em 0.5em 0',
    backgroundColor: theme.paintscout.palette.brand.Neutral.P94
  },
  tile: {
    height: '4em',
    width: '16em',
    marginBottom: '0.5em'
  },
  dropdown: {
    width: '9em',
    marginBottom: '0.5em'
  },
  dropdownLong: {
    width: '13em',
    marginBottom: '0.5em'
  },
  locationsGrid: {
    marginBottom: '1em'
  },
  input: {
    width: '4em',
    marginBottom: theme.spacing(1)
  },
  closeButton: {
    position: 'absolute',
    top: '0.01em',
    right: '1em'
  }
}))

export interface AddRemoveSectionProps {
  id: string
  quote: QuoteDocument
  options: OptionsDocument
  bulkDetails: BulkEditDetails
  handleActionChange: (operation: BulkOperation) => any
  handleSectionClose: (id: string) => any
  checkParentHasChanges?: () => boolean
}

function AddRemoveSection(props: AddRemoveSectionProps) {
  const { id, quote, bulkDetails, options, handleActionChange, handleSectionClose } = props
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const classes = useStyles(props)
  const [operation, setOperation] = useState<BulkOperation>({ key: id })
  const [selectedActionOption, setSelectedActionOption] = useState(null)
  const [selectedUpdateOption, setSelectedUpdateOption] = useState(null)
  const objectLabels = getObjectLabels({ options })
  const hasLineItems = bulkDetails.editItems.some((editItem) => editItem.type === 'line-item')
  const hasSubstrates = bulkDetails.editItems.some((editItem) => editItem.type === 'substrate')
  const actionOptions = [
    { value: 'add', label: 'Add' },
    { value: 'remove', label: 'Remove' }
  ]

  const updateOptions = [
    hasLineItems && hasSubstrates ? { value: 'all', label: 'All' } : null,
    hasLineItems ? { value: LINE_ITEMS_VALUE, label: 'Line Items' } : null,
    hasSubstrates ? { value: 'substrates', label: objectLabels.substrate.value } : null
  ].filter(Boolean)

  const removalOptions = bulkDetails.uniqueProducts.map((product) => {
    return { value: product, label: product }
  })

  // Pass our updated operation to our outer list on update if started
  useEffect(() => {
    if (operation.lineItems || operation.substrates || operation.newProduct || operation.oldProduct) {
      handleActionChange(operation)
    }
  }, [operation])

  const handleChangeAction = (item: DropdownSelectOption) => {
    const selectedOption = actionOptions.find((option) => option.value === item.value)
    setSelectedActionOption(selectedOption)
    if (item.value === 'add') {
      // Reset state for item addition
      setOperation({ ...operation, newProduct: null, oldProduct: 'ADD_NEW_PRODUCT' })
    } else {
      // Reset state for item removal
      setOperation({ ...operation, oldProduct: null, newProduct: { label: 'REMOVE_PRODUCT' } as ProductOption })
    }
  }

  const handleChangeOldProduct = (item: DropdownSelectOption) => {
    setOperation({ ...operation, oldProduct: String(item.label) })
  }

  const handleChooseMaterial = () => {
    return openDialog(ProductSelectDialog, {
      options,
      checkParentHasChanges: props.checkParentHasChanges,
      onConfirm: (productOption: ProductOption) => {
        const newOperation = { ...operation, newProduct: productOption }
        setOperation(newOperation)
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      },
      quoteType: quote.type
    })
  }

  const handleChangeAddQuantity = (e: any) => {
    setOperation({
      ...operation,
      newProduct: {
        ...operation.newProduct,
        quantity: e.target.value
      }
    })
  }

  const handleChangeColorValue = (e: any) => {
    setOperation({
      ...operation,
      newProduct: {
        ...operation.newProduct,
        color: e.target.value
      }
    })
  }

  const handleChangeAddTarget = (item: DropdownSelectOption) => {
    const lineItems = []
    const substrates = []
    const selectedOption = updateOptions.find((option) => option.value === item.value)
    setSelectedUpdateOption(selectedOption)
    if (item.value === 'all') {
      // if all, set lineItems to all lineItmes, substrates to all substrates
      bulkDetails.editItems.forEach((location) => {
        if (location.type === 'line-item') {
          lineItems.push(location)
        } else if (location.type === 'substrate') {
          substrates.push(location)
        }
      })
      const newOperation = { ...operation, lineItems, substrates }
      setOperation(newOperation)
    } else if (item.value === LINE_ITEMS_VALUE) {
      // if line-items, add all lineItems, clear substrates to blank
      bulkDetails.editItems.forEach((location) => (location.type === 'line-item' ? lineItems.push(location) : null))
      const newOperation = { ...operation, lineItems, substrates: [] }
      setOperation(newOperation)
    } else if (item.value === 'substrates') {
      // if substrates, add all substrates, clear lineitems to blank
      bulkDetails.editItems.forEach((location) => (location.type === 'substrate' ? substrates.push(location) : null))
      const newOperation = { ...operation, lineItems: [], substrates }
      setOperation(newOperation)
    }
  }

  const handleClear = () => {
    setOperation({ key: operation.key })
    setSelectedActionOption(null)
    setSelectedUpdateOption(null)
    handleActionChange({ key: operation.key })
  }

  const handleClose = () => {
    handleSectionClose(operation.key)
  }

  return (
    <Grid container={true} item={true} xs={12} className={classes.editItemSection}>
      <Grid item={true} xs={12} sm={3}>
        <DropdownSelect
          variant="single"
          label={'Action'}
          options={actionOptions}
          className={classes.dropdown}
          value={selectedActionOption}
          onChange={(item: DropdownSelectOption) => handleChangeAction(item)}
        />
      </Grid>
      {operation.newProduct?.label === 'REMOVE_PRODUCT' && (
        // If we set newProduct, we need to choose and oldproduct to remove
        <Grid item={true} xs={12} sm={4}>
          <DropdownSelect
            variant="single"
            label={'Material'}
            value={null}
            options={removalOptions}
            className={classes.dropdownLong}
            onChange={(item: DropdownSelectOption) => handleChangeOldProduct(item)}
          />
        </Grid>
      )}
      {operation.oldProduct === 'ADD_NEW_PRODUCT' && (
        // if we set oldProduct, we need to choose a newProcuct to add
        <Grid item={true} xs={12} sm={4}>
          {/* SHow a select material tile here */}
          <Tile onClick={() => handleChooseMaterial()} className={classes.tile}>
            {operation.newProduct && <label>{operation.newProduct?.label}</label>}
            {!operation.newProduct && <label>Select...</label>}
          </Tile>
        </Grid>
      )}

      {operation.newProduct?.label && operation.oldProduct && (
        <>
          <Grid item={true} xs={12} sm={4}>
            <DropdownSelect
              variant="single"
              label={'Apply To'}
              options={updateOptions}
              className={classes.dropdownLong}
              value={selectedUpdateOption}
              onChange={(item: DropdownSelectOption) => handleChangeAddTarget(item)}
            />
          </Grid>
          {operation.newProduct.label !== 'REMOVE_PRODUCT' &&
            selectedUpdateOption?.value &&
            selectedUpdateOption.value !== 'substrates' &&
            hasLineItems && (
              <Grid className={classes.input} item={true} xs={12} sm={4}>
                <InputField
                  value={operation?.newProduct?.quantity}
                  className={classes.input}
                  name={'quantity'}
                  type={'number'}
                  label={'Qty'}
                  onChange={(e: any) => handleChangeAddQuantity(e)}
                />
              </Grid>
            )}
          {operation.newProduct.label !== 'REMOVE_PRODUCT' &&
            selectedUpdateOption?.value &&
            selectedUpdateOption.value !== LINE_ITEMS_VALUE &&
            hasSubstrates && (
              <Grid className={classes.input} item={true} xs={12} sm={4}>
                <InputField
                  value={operation?.newProduct?.color}
                  className={classes.input}
                  name={'color'}
                  type={'string'}
                  label={'Color'}
                  onChange={(e: any) => handleChangeColorValue(e)}
                />
              </Grid>
            )}
        </>
      )}
      {(operation.lineItems || operation.substrates || operation.newProduct || operation.oldProduct) && (
        <Grid item={true} xs={12}>
          <Button onClick={handleClear}>Clear</Button>
        </Grid>
      )}
      <CloseButton className={classes.closeButton} onCancel={() => handleClose()} />
    </Grid>
  )
}

export default AddRemoveSection
